// src/templates/industries/DynamicResume.jsx
import React from 'react';

const DynamicResume = ({ data = {}, sections = [], industry }) => {
  const { personalInfo = {}, skills = [], experience = [], education = [] } = data;

  // Helper to render section content based on type
  const renderSectionContent = (section, value) => {
    if (!value) return null;
    if (Array.isArray(value) && value.length === 0) return null;
    if (typeof value === 'object' && Object.keys(value).length === 0) return null;

    switch (section.type) {
      case 'skills':
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {value.map((item, idx) => (
              <span key={idx} style={{ 
                background: '#ecf0f1', 
                padding: '4px 12px', 
                borderRadius: '20px', 
                fontSize: '13px' 
              }}>
                {typeof item === 'string' ? item : item.name || item}
              </span>
            ))}
          </div>
        );

      case 'flyingHours':
        const fieldLabels = {
          totalHours: 'Total Hours',
          multiEngine: 'Multi-Engine Hours',
          instrument: 'Instrument Hours',
          night: 'Night Hours',
          crossCountry: 'Cross-Country Hours',
          turbine: 'Turbine Hours'
        };
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
            {Object.entries(value).map(([key, val]) => (
              <div key={key} style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>{fieldLabels[key] || key}</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50' }}>{val || 0}</div>
              </div>
            ))}
          </div>
        );

      case 'text':
        return <p style={{ margin: 0 }}>{value}</p>;

      case 'textarea':
        return <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</p>;

      case 'experience':
      case 'flight-experience':
        return value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid #3498db' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <strong>{item.position || item.title || item.airline}</strong>
              <span style={{ color: '#666' }}>{item.company || item.airline}</span>
              <span style={{ color: '#666', fontSize: '13px' }}>{item.dates || item.startDate}</span>
            </div>
            {item.responsibilities && Array.isArray(item.responsibilities) && (
              <ul style={{ margin: '4px 0 0 16px', fontSize: '14px', color: '#555' }}>
                {item.responsibilities.map((resp, i) => (
                  <li key={i}>{resp}</li>
                ))}
              </ul>
            )}
            {item.description && <p style={{ fontSize: '14px', margin: '4px 0 0' }}>{item.description}</p>}
          </div>
        ));

      case 'projects':
        return value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <strong>{item.projectName || item.name}</strong>
            {item.description && <span style={{ color: '#666', marginLeft: '8px' }}>{item.description}</span>}
            {item.technologies && (
              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {item.technologies.split(',').map((tech, i) => (
                  <span key={i} style={{ background: '#e9ecef', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                    {tech.trim()}
                  </span>
                ))}
              </div>
            )}
            {item.outcome && <div style={{ fontSize: '13px', color: '#28a745', marginTop: '4px' }}>✅ {item.outcome}</div>}
          </div>
        ));

      case 'publications':
        return value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '6px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <strong>{item.title}</strong>
            {item.journal && <span style={{ color: '#666', marginLeft: '8px' }}>{item.journal}</span>}
            {item.year && <span style={{ color: '#666', fontSize: '13px', marginLeft: '8px' }}>({item.year})</span>}
            {item.doi && <span style={{ fontSize: '12px', color: '#3498db', display: 'block' }}>DOI: {item.doi}</span>}
          </div>
        ));

      case 'education':
        return value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '6px' }}>
            <strong>{item.degree}</strong>
            {item.institution && <span style={{ color: '#666', marginLeft: '8px' }}>{item.institution}</span>}
            {item.year && <span style={{ color: '#666', fontSize: '13px', marginLeft: '8px' }}>({item.year})</span>}
            {item.honors && <div style={{ fontSize: '13px', color: '#666', marginLeft: '12px' }}>🏅 {item.honors}</div>}
          </div>
        ));

      case 'certifications':
        return value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '4px' }}>
            <strong>{item.name}</strong>
            {item.issuer && <span style={{ color: '#666', marginLeft: '8px' }}>{item.issuer}</span>}
            {item.year && <span style={{ color: '#666', fontSize: '13px', marginLeft: '8px' }}>({item.year})</span>}
          </div>
        ));

      default:
        return <p style={{ color: '#666' }}>{JSON.stringify(value)}</p>;
    }
  };

  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif',
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '40px',
      background: '#ffffff',
      color: '#333',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      borderRadius: '8px'
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        borderBottom: '3px solid #2c3e50', 
        paddingBottom: '20px', 
        marginBottom: '24px' 
      }}>
        {personalInfo?.photo_url && (
          <img 
            src={personalInfo.photo_url} 
            alt="Profile" 
            style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              objectFit: 'cover',
              border: '3px solid #2c3e50',
              marginBottom: '10px'
            }} 
          />
        )}
        <h1 style={{ fontSize: '32px', margin: '5px 0', color: '#2c3e50' }}>
          {personalInfo?.full_name || 'Your Name'}
        </h1>
        <p style={{ fontSize: '18px', color: '#666', marginBottom: '8px' }}>
          {personalInfo?.title || 'Professional Title'}
        </p>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px', 
          flexWrap: 'wrap', 
          fontSize: '14px',
          color: '#555'
        }}>
          {personalInfo?.email && <span>📧 {personalInfo.email}</span>}
          {personalInfo?.phone && <span>📱 {personalInfo.phone}</span>}
          {personalInfo?.address && <span>📍 {personalInfo.address}</span>}
        </div>
      </div>

      {/* Summary */}
      {personalInfo?.summary && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            Professional Summary
          </h3>
          <p style={{ lineHeight: '1.6', color: '#444' }}>{personalInfo.summary}</p>
        </div>
      )}

      {/* Experience */}
      {experience.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            Work Experience
          </h3>
          {experience.map((exp, i) => (
            <div key={i} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <strong>{exp.title}</strong>
                <span style={{ color: '#3498db' }}>{exp.company}</span>
                <span style={{ color: '#666', fontSize: '14px' }}>{exp.startDate} - {exp.endDate}</span>
              </div>
              {exp.description && <p style={{ fontSize: '14px', color: '#555', margin: '4px 0 0' }}>{exp.description}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {education.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            Education
          </h3>
          {education.map((edu, i) => (
            <div key={i} style={{ marginBottom: '4px' }}>
              <strong>{edu.degree}</strong>
              {edu.institution && <span style={{ color: '#666', marginLeft: '8px' }}>{edu.institution}</span>}
              {edu.year && <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>({edu.year})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            Skills
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {skills.map((skill, i) => (
              <span key={i} style={{ 
                background: '#ecf0f1', 
                padding: '4px 14px', 
                borderRadius: '20px', 
                fontSize: '14px',
                color: '#2c3e50'
              }}>
                {typeof skill === 'string' ? skill : skill.name || skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Industry-Specific Sections */}
      {sections && sections.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            {industry?.name || 'Industry'} Details
          </h3>
          
          {sections.map((section) => {
            // Skip standard sections that are already rendered above
            const standardTypes = ['summary', 'education', 'experience', 'skills'];
            if (standardTypes.includes(section.id)) return null;

            const value = data[section.id];
            if (!value) return null;
            if (Array.isArray(value) && value.length === 0) return null;
            if (typeof value === 'object' && Object.keys(value).length === 0) return null;

            return (
              <div key={section.id} style={{ marginBottom: '16px' }}>
                <h4 style={{ 
                  fontSize: '15px', 
                  fontWeight: '600',
                  color: '#2c3e50',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{section.icon}</span> {section.label}
                  {section.required && <span style={{ color: '#e74c3c', fontSize: '12px' }}>*</span>}
                </h4>
                <div style={{ paddingLeft: '8px' }}>
                  {renderSectionContent(section, value)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certifications - If not already in sections */}
      {data.certifications && data.certifications.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h3 style={{ 
            borderBottom: '2px solid #3498db', 
            paddingBottom: '6px',
            color: '#2c3e50',
            fontSize: '18px'
          }}>
            Certifications
          </h3>
          {data.certifications.map((cert, i) => (
            <div key={i}>
              <strong>{cert.name}</strong>
              {cert.issuer && <span style={{ color: '#666', marginLeft: '8px' }}>{cert.issuer}</span>}
              {cert.year && <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>({cert.year})</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DynamicResume;